import { GridColumns, GridValueGetterParams } from "@mui/x-data-grid";
import React from "react";
import { useTranslation } from "react-i18next";
import QuickfiltersContainer from "../../../containers/Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { StatusQuickfilter } from "../../../containers/Quickfilters/StatusesQuickfilter/StatusQuickfilter";
import { useStatusQuickFilter } from "../../../containers/Quickfilters/StatusesQuickfilter/useStatusQuickFilter";
import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import { PropsFilter } from "../../../models/api/props-filter/props-filter";
import { RecruiterDTO } from "../../../models/recruiting/recruiters/recruiter-dto";
import { renderCellExpand } from "../../../reusable-components/RenderCellExpand/RenderCellExpand";
import { FormField } from "../../../reusable-components/Table/AddEditItem/interfaces";
import Table from "../../../reusable-components/Table/Table";
import { RecruitersApiService } from "../../../services/recruiting/recruiters/recruiters-api.service";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import { FT_RT } from "../../../utils/constants/filter-tables-constants";
import { intlFormatDateShort } from "../../../utils/time-utils";

const Recruiters = () => {
  const { t } = useTranslation();
  const tRP = "pages.system_settings.tables.recruiters";
  const translationFields = `${tRP}.fields`;

  const isAllowedEditAddDelete = useAuthorization([AUTHORIZATIONS.RecruitingAccess.CanCreateRecruiters]);

  const { statusQuickFilter, setStatusQuickFilter } = useStatusQuickFilter(false);

  const columns: GridColumns = [
    {
      field: "recruiterCode",
      valueGetter: ({ row }) => row?.recruiterCode,
      headerName: t(`${translationFields}.recruiter_code`),
      headerAlign: "center",
      flex: 1,
      maxWidth: 120,
    },
    {
      field: "name",
      valueGetter: ({ row }) => row?.name,
      headerName: t(`${translationFields}.name`),
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "contactDetails",
      valueGetter: ({ row }) => row?.contactDetails,
      headerName: t(`${translationFields}.contact_details`),
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "isInactive",
      valueGetter: ({ row }) => (row?.isInactive ? "Inactive" : "Active"),
      headerName: t(`${translationFields}.status`),
      headerAlign: "center",
      flex: 1,
      maxWidth: 100,
      align: "center",
    },
    {
      field: "comment",
      valueGetter: ({ row }) => row?.comment,
      headerName: t(`${translationFields}.comment`),
      headerAlign: "center",
      flex: 1,
      renderCell: renderCellExpand,
    },
    {
      field: "createdBy",
      valueGetter: ({ row }) => row?.createdByUser?.name ?? row?.createdByUser?.email,
      headerName: t(`${translationFields}.created_by`),
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "createdOn",
      valueGetter: (params: GridValueGetterParams) => intlFormatDateShort(new Date(params?.row["createdAt"])),
      headerName: t(`${translationFields}.created_on`),
      headerAlign: "center",
      flex: 1,
      maxWidth: 110,
      align: "center",
    },
  ];

  const formFields: FormField[] = [
    {
      labelKey: t(`${translationFields}.recruiter_code`),
      field: "recruiterCode",
      fieldType: "textfield",
    },
    {
      labelKey: t(`${translationFields}.name`),
      field: "name",
      fieldType: "textfield",
    },
    {
      labelKey: t(`${translationFields}.contact_details`),
      field: "contactDetails",
      fieldType: "textfield",
    },
    {
      labelKey: t(`${translationFields}.status`),
      field: "isInactive",
      fieldType: "checkbox",
    },
    {
      labelKey: t("common_words.comment"),
      field: "comment",
      fieldType: "textarea",
    },
  ];

  const defaultItem: Partial<RecruiterDTO> = {
    recruiterCode: "",
    name: "",
    contactDetails: "",
    isInactive: false,
    comment: "",
  };

  return (
    <Table
      translationRootPath={tRP}
      columns={columns}
      serviceApi={new RecruitersApiService()}
      formFields={formFields}
      defaultItem={defaultItem}
      filter={FT_RT}
      readOnly={!isAllowedEditAddDelete}
      propsFilter={
        {
          isInactive: statusQuickFilter.statuses.isTrueOrFalse,
        } as PropsFilter
      }
      quickFilters={
        <QuickfiltersContainer>
          <StatusQuickfilter
            quickFilters={statusQuickFilter}
            handleChangeQuickFilters={setStatusQuickFilter}
            tab={"other"}
          />
        </QuickfiltersContainer>
      }
    />
  );
};

export default Recruiters;
